import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './pages/Login';
import TechLogin from './pages/TechLogin';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import Dashboard from './pages/Dashboard';
import Teams from './pages/Teams';
import TeamDetail from './pages/TeamDetail';
import Tasks from './pages/Tasks';
import TaskDetail from './pages/TaskDetail';
import Users from './pages/Users';
import MyTasks from './pages/MyTasks';
import TechDashboard from './pages/TechDashboard';
import TechTasks from './pages/TechTasks';
import TechTaskDetail from './pages/TechTaskDetail';
import TechPipeline from './pages/TechPipeline';
import TechTeam from './pages/TechTeam';
import TechTimesheet from './pages/TechTimesheet';
import Layout from './components/Layout';
import TechLayout from './components/TechLayout';

const Spinner = ({ dark }) => (
  <div
    className={`min-h-screen flex items-center justify-center ${
      dark ? 'bg-slate-950' : 'bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700'
    }`}
  >
    <div
      className={`animate-spin rounded-full h-12 w-12 border-b-2 ${dark ? 'border-teal-400' : 'border-white'}`}
    />
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading, isTechWorkspace } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (isTechWorkspace) return <Navigate to="/tech" replace />;
  return children;
};

const TechPrivateRoute = ({ children }) => {
  const { user, loading, isTechWorkspace } = useAuth();
  if (loading) return <Spinner dark />;
  if (!user) return <Navigate to="/tech-login" replace />;
  if (!isTechWorkspace) return <Navigate to="/" replace />;
  return children;
};

const GuestRoute = ({ children, tech }) => {
  const { user, loading, isTechWorkspace } = useAuth();
  if (loading) return <Spinner dark={tech} />;
  if (user && isTechWorkspace) return <Navigate to="/tech" replace />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <Routes>
              <Route
                path="/login"
                element={
                  <GuestRoute>
                    <Login />
                  </GuestRoute>
                }
              />
              <Route
                path="/tech-login"
                element={
                  <GuestRoute tech>
                    <TechLogin />
                  </GuestRoute>
                }
              />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route
                path="/tech/*"
                element={
                  <TechPrivateRoute>
                    <TechLayout>
                      <Routes>
                        <Route index element={<TechDashboard />} />
                        <Route path="tasks" element={<TechTasks />} />
                        <Route path="tasks/:id" element={<TechTaskDetail />} />
                        <Route path="timesheet" element={<TechTimesheet />} />
                        <Route path="pipeline" element={<TechPipeline />} />
                        <Route path="users" element={<TechTeam />} />
                        <Route path="team" element={<Navigate to="/tech/users" replace />} />
                        <Route path="*" element={<Navigate to="/tech" replace />} />
                      </Routes>
                    </TechLayout>
                  </TechPrivateRoute>
                }
              />
              <Route
                path="/*"
                element={
                  <PrivateRoute>
                    <Layout>
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/teams" element={<Teams />} />
                        <Route path="/teams/:id" element={<TeamDetail />} />
                        <Route path="/tasks" element={<Tasks />} />
                        <Route path="/tasks/:id" element={<TaskDetail />} />
                        <Route path="/my-tasks" element={<MyTasks />} />
                        <Route path="/users" element={<Users />} />
                      </Routes>
                    </Layout>
                  </PrivateRoute>
                }
              />
            </Routes>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
